import PropTypes from "prop-types";
import React, { useState } from "react";
import toast from "react-hot-toast";
import { useSearchParams } from "react-router-dom";
import { Service } from "rsl-api-106/lib/types";

import Alert from "@components/_Layout/Alert";
import Button from "@components/_Layout/Controls/Button";
import Login from "@components/Auth/Login";
import { LoginPartner } from "@components/Auth/Login/Login";
import Register from "@components/Auth/Register";
import CustomerDetails from "@components/Customer/CustomerDetails";
import PartnerDetails from "@components/Partner/PartnerDetails";
import { useAuth, useCart, useScrollTop, useSession } from "@hooks";
import { Accordion, Typography } from "@mui/material";
import MuiAccordionDetails from "@mui/material/AccordionDetails";
import MuiAccordionSummary, {
  AccordionSummaryProps,
} from "@mui/material/AccordionSummary";
import { styled } from "@mui/material/styles";
import { getAbsoluteImagePath } from "@utils/dom";
import {
  sendDataLayerInscription,
  sendDataLayerLogin,
} from "@utils/tracking/gtm";

import { Connexion_Type } from "../../../../types";

type StepAuthProps = {
  onNextStep: () => void;
  showContinueWithoutLogin: boolean;
};

const languagesVarsCodes = [
  "LANG_neobooking_logout_in_progress", //Déconnexion en cours...
  "LANG_neobooking_logout", //Déconnexion
  "LANG_neobooking_logout_successfully", //Vous êtes maintenant déconnecté !
  "LANG_neobooking_isnt_you", //Ce n'est pas vous ?
  "LANG_neobooking_i_already_have_account", //J'ai déjà un compte
  "LANG_neobooking_i_new_client", //Je suis nouveau client
  "LANG_neobooking_logged_as", //Vous êtes connecté en tant que:
  "LANG_neobooking_continue_with_login", //Continuer sans se connecter
];

const StepAuth: React.FC<StepAuthProps> = ({
  onNextStep,
  showContinueWithoutLogin,
}) => {
  /* Hooks */
  const auth = useAuth();
  const { getLanguageVarValue, authMode } = useSession();
  const [searchParams, setSearchParams] = useSearchParams();
  const origin_url = searchParams.get("originUrl");

  /* State */
  const [isSubmittingSignout, setIsSubmittingSignout] =
    useState<boolean>(false);

  useScrollTop();

  const handleSignout = () => {
    setIsSubmittingSignout(true);
    const toastLogout = toast.loading(
      getLanguageVarValue("LANG_neobooking_logout_in_progress")
    );
    auth.signout((err) => {
      if (!err) {
        toast.success(
          getLanguageVarValue("LANG_neobooking_logout_successfully"),
          {
            id: toastLogout,
          }
        );
        //redirectTo(origin_url ? origin_url : window.location.origin);
        setIsSubmittingSignout(false);
      } else
        toast.error(err.message, {
          id: toastLogout,
        });
    });
  };

  return authMode === 2 ? (
    <StepAuthPartner onEditSuccess={onNextStep} />
  ) : (
    <>
      {auth.user ? (
        <>
          <Alert dismissable={false} variant="primary">
            <span className="tw-block">
              {getLanguageVarValue("LANG_neobooking_logged_as")}{" "}
              <b>
                {auth.user.firstName} {auth.user.lastName}
              </b>
            </span>
            <span>
              {getLanguageVarValue("LANG_neobooking_isnt_you")}
              <Button
                isLoading={isSubmittingSignout}
                loadingChildren={
                  <>
                    {getLanguageVarValue("LANG_neobooking_logout_in_progress")}
                  </>
                }
                additionalClassname="tw-ml-2"
                onClick={handleSignout}
                shape="link"
              >
                {getLanguageVarValue("LANG_neobooking_logout")}
              </Button>
            </span>
          </Alert>
        </>
      ) : (
        <>
          <AccordionAuth onNextStep={onNextStep} />
          {showContinueWithoutLogin && showContinueWithoutLogin && (
            <div className="tw-mt-4 tw-text-right md:tw-text-left">
              <button
                className="tw-underline tw-font-dosis tw-font-semibold tw-text-xl tw-underline-offset-[6px]"
                onClick={onNextStep}
              >
                {getLanguageVarValue("LANG_neobooking_continue_with_login")}
              </button>
            </div>
          )}
        </>
      )}
    </>
  );
};

type StepAuthPartnerProps = {
  onEditSuccess?: () => void;
};
export const StepAuthPartner: React.FC<StepAuthPartnerProps> = ({
  onEditSuccess,
}) => {
  const { getLanguageVarValue } = useSession();
  const { cart } = useCart();
  const { user } = useAuth();
  return !user ? (
    <Accordion expanded={true}>
      <AccordionSummary>
        <Typography>
          <span className="tw-titre-principal-dosis">
            {getLanguageVarValue("LANG_neobooking_login_partner")}
          </span>
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <LoginPartner />
      </AccordionDetails>
    </Accordion>
  ) : (
    <PartnerDetails
      user={cart?.reservations?.[0]?.primaryOccupant}
      onEditSuccess={onEditSuccess}
    />
  );
};

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary
    expandIcon={
      <img
        className="tw-w-5 tw-h-5"
        src={getAbsoluteImagePath(`/assets/img/icons/carret_down.svg`)}
      />
    }
    {...props}
  />
))(({ theme }) => ({
  backgroundColor: "white",
  border: "none",
  boxShadow: "0 3px 10px 2px rgb(0 0 0 / 20%)",
  "&.Mui-expanded.MuiAccordionSummary-root": {
    boxShadow: "none",
  },
  flexDirection: "revert",
  "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
    transform: "rotate(180deg)",
  },
  "& .MuiAccordionSummary-content": {
    marginLeft: theme.spacing(1),
  },
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: "1px solid rgba(0, 0, 0, .125)",
}));

type AccordionAuthProps = {
  onNextStep: () => void;
};

const AccordionAuth: React.FC<AccordionAuthProps> = ({ onNextStep }) => {
  /* Hooks */
  const { funnel_type, getLanguageVarValue } = useSession();
  const { cart } = useCart();

  /* State */
  const [expanded, setExpanded] =
    React.useState<"panel_login" | "panel_register" | false>(false);

  /* Functions */
  const handleChange =
    (panel: "panel_login" | "panel_register") =>
    (event: React.SyntheticEvent, newExpanded: boolean) => {
      setExpanded(newExpanded ? panel : false);
    };

  const onAuthenticated = (
    service: Service | null,
    connexion_type: Connexion_Type
  ) => {
    if (connexion_type === "login") {
      sendDataLayerLogin(cart, funnel_type);
    } else {
      sendDataLayerInscription(cart, funnel_type, service);
    }
    onNextStep();
  };

  return (
    <div className="tw-flex tw-flex-col tw-gap-2">
      <Accordion
        expanded={expanded === "panel_register"}
        onChange={handleChange("panel_register")}
      >
        <AccordionSummary
          aria-controls="panel_registerd-content"
          id="panel_registerd-header"
        >
          <Typography>
            <span className="tw-titre-principal-dosis">
              {getLanguageVarValue("LANG_neobooking_i_new_client")}
            </span>
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Register
            onRegisterSuccess={(service) =>
              onAuthenticated(service, "register")
            }
          />
        </AccordionDetails>
      </Accordion>
      <Accordion
        expanded={expanded === "panel_login"}
        onChange={handleChange("panel_login")}
      >
        <AccordionSummary
          aria-controls="panel_logind-content"
          id="panel_logind-header"
        >
          <Typography>
            <span className="tw-titre-principal-dosis">
              {getLanguageVarValue("LANG_neobooking_i_already_have_account")}
            </span>
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Login
            onLoginSuccess={(service) => onAuthenticated(service, "login")}
          />
        </AccordionDetails>
      </Accordion>
    </div>
  );
};

StepAuth.propTypes = {
  //
};

export default StepAuth;
